import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { ConfirmDialog } from './ui'

interface ConfirmOptions {
  title: string
  description: string
  confirmLabel?: string
  tone?: 'primary' | 'danger'
}
interface PromptOptions extends ConfirmOptions { minLength?: number }
type Pending = {
  kind: 'confirm' | 'prompt'
  options: PromptOptions
  resolve: (value: boolean | string | null) => void
}
interface ConfirmationContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
  prompt: (options: PromptOptions) => Promise<string | null>
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null)

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)
  const [reason, setReason] = useState('')
  const ask = useCallback((kind: Pending['kind'], options: PromptOptions) => new Promise<boolean | string | null>((resolve) => {
    setReason(''); setPending((current) => {
      if (current) { resolve(kind === 'confirm' ? false : null); return current }
      return { kind, options, resolve }
    })
  }), [])
  const close = useCallback(() => setPending((current) => {
    current?.resolve(current.kind === 'confirm' ? false : null); return null
  }), [])
  const accept = useCallback(() => setPending((current) => {
    current?.resolve(current.kind === 'confirm' ? true : reason.trim()); return null
  }), [reason])
  const value = useMemo<ConfirmationContextValue>(() => ({
    confirm: (options) => ask('confirm', options) as Promise<boolean>,
    prompt: (options) => ask('prompt', options) as Promise<string | null>,
  }), [ask])
  return <ConfirmationContext.Provider value={value}>{children}<ConfirmDialog open={Boolean(pending)} title={pending?.options.title ?? ''} description={pending?.options.description ?? ''} confirmLabel={pending?.options.confirmLabel} tone={pending?.options.tone} reason={pending?.kind === 'prompt' ? reason : undefined} minReasonLength={pending?.kind === 'prompt' ? pending.options.minLength ?? 3 : 0} onReasonChange={pending?.kind === 'prompt' ? setReason : undefined} onConfirm={accept} onClose={close} /></ConfirmationContext.Provider>
}

export function useConfirmation() {
  const value = useContext(ConfirmationContext)
  if (!value) throw new Error('useConfirmation must be used inside ConfirmationProvider')
  return value
}
